import { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ObraProperties, WandaProject } from '../../../types/ObraDetails';
import TitleValue from '../components/titleValue/titleValue';

import styles from './dates.module.scss';
import { UserModeContext } from '../../../context/UserModeProvider';
import { SaveContext } from '../../../context/SaveProvider';
import moment from 'moment';
import InformationIcon from '../components/informationIcon/informationIcon';
import ObrasAPI from '../../../services/APIObras';
import ErrorDetails from '../components/ErrorDetails/ErrorDetails';
import { DatesLoadingSkeleton } from './components/datesLoadingSkeleton/datesLoadingSkeleton';
import { DetailsViewContext } from '../../../context/DetailsViewProvider';

interface DatesProps {
  obra: ObraProperties;
  codCentro: string;
  wandaProject?: WandaProject;
  wandaProjectLoading: boolean;
  wandaProjectError: boolean;
  version: 'viewer' | 'editor';
  errors?: Array<{ field?: string }>;
}

type ObraFechasKeys = 'fecActaRecepcion' | 'fecFinEjecucion';

/* eslint-disable @typescript-eslint/no-non-null-assertion */
const Dates: FC<DatesProps> = (props) => {
  const {
    obra,
    codCentro,
    wandaProject,
    wandaProjectLoading,
    wandaProjectError,
    version,
    errors,
  } = props;
  const [t, i18n] = useTranslation();
  const userModeContext = useContext(UserModeContext);
  const {
    setObra,
    loadedDetailSections,
    markDetailSectionLoaded,
  } = useContext(DetailsViewContext);
  const isEditorMode = userModeContext.userMode === 'editor';
  const saveContext = useContext(SaveContext);
  const [datesLoaded, setDatesLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const sectionKey = `${codCentro}:${version}:${i18n.language}:dates`;

  useEffect(() => {
    let active = true;
    setDatesLoaded(false);
    setLoadError(false);
    if (!wandaProject) return () => {
      active = false;
    };

    if (loadedDetailSections[sectionKey]) {
      setDatesLoaded(true);
      return () => {
        active = false;
      };
    }

    ObrasAPI.getInstance().getObraDatesAPI(codCentro, wandaProject, version)
      .then((response) => {
        if (!active) return;
        setObra({
          ...obra,
          fechasPlazos: response.fechasPlazos,
          presupuesto: {
            ...obra.presupuesto,
            fecAdjudicacion: response.fecAdjudicacion,
          },
        });
        markDetailSectionLoaded(sectionKey);
        setDatesLoaded(true);
      })
      .catch(() => {
        if (active) setLoadError(true);
      });

    return () => {
      active = false;
    };
  }, [codCentro, wandaProject, version, i18n.language, saveContext.reloadData, loadedDetailSections, markDetailSectionLoaded, sectionKey]);

  if (loadError || wandaProjectError) return <ErrorDetails />;
  if (!datesLoaded || wandaProjectLoading) return <DatesLoadingSkeleton />;

  const invalidValues = ['0 Meses', '0 Months', '0 Mêses', '0 Miesiące', '0 Mes/es', '0 Month/s', '0 Mês/es', '0 Miesiąc/e/y', ''];
  const finishTime = obra.fechasPlazos.fecFinalizacionActualizada ? moment(obra.fechasPlazos.fecFinalizacionActualizada, 'DD/MM/YYYY') : moment(obra.fechasPlazos.fecFinalizacionInicial, 'DD/MM/YYYY');
  const isOnTime = moment().isAfter(finishTime.subtract(30, 'days'));
  const plazoInicialValue = obra.fechasPlazos.plazoInicial.toString().replace('.', ',');
  const tipoPlazoInicial = (obra.fechasPlazos.tipoPlazoInicial.includes('/')) ? obra.fechasPlazos.tipoPlazoInicial.replace('/', '')  : obra.fechasPlazos.tipoPlazoInicial;
  const plazoInicial = obra.fechasPlazos.plazoInicial ? `${plazoInicialValue} ${tipoPlazoInicial}` : t('details.notInformed');
  const plazoActualizado = obra.fechasPlazos.plazoActualizado
    ? obra.fechasPlazos.plazoActualizado.toString().replace('.', ',')
    : '';
  const plazoActualizadoValue = invalidValues.includes(plazoActualizado) || !plazoActualizado
    ? t('details.notInformed')
    : plazoActualizado;

  const handleChange = (field: string, value: string) => {
    saveContext.handleChangeSave(true);
    setObra({
      ...obra,
      fechasPlazos: {
        ...obra.fechasPlazos,
        [field]: value,
      },
    });
  };

  const isFieldError = (field: string) => {
    return !!(errors && errors.find((e) => e.field === field));
  };

  const isEditable = (field: ObraFechasKeys): boolean => {
    return isEditorMode && (isOnTime || !!obra.fechasPlazos[field]);
  };

  return (

    <div data-testid="dates-test">
      {
        <div className={styles.datesContainer}>
          <div id='dates-start' className={styles.heading}>{t('details.dates.start')}
            <InformationIcon message={t('help.dates.start')} />
          </div>
          <div className={styles.datesStart}>
            <TitleValue identifier='dates-award' title={t('details.dates.award')} value={obra.presupuesto.fecAdjudicacion || t('details.notInformed')} viewStyle={{padding: '0 16px'}}/>
            <TitleValue identifier='dates-contractSignature' title={t('details.dates.contractSignature')} value={obra.fechasPlazos.fecFirmaContrato || t('details.notInformed')} viewStyle={{padding: '0 16px'}}/>
            <TitleValue type='date' handleChange={(data) => handleChange('fecActaReplanteo', data)} error={isFieldError('fecActaReplanteo')} minDate={null} maxDate={null} editable={isEditorMode} identifier='dates-reconsideration' title={t('details.dates.reconsideration')} value={obra.fechasPlazos.fecActaReplanteo || '--'} viewStyle={{padding: '0 16px'}}/>
            <TitleValue type='date' handleChange={(data) => handleChange('fecInicio', data)} error={isFieldError('fecInicio')} minDate={null} maxDate={null} editable={isEditorMode} identifier='dates-start' title={t('details.dates.start')} value={obra.fechasPlazos.fecInicio || '--'} viewStyle={{padding: '0 16px'}}/>
          </div>
          <div id='dates-deadlines' className={styles.heading}>{t('details.dates.deadlines')}
            <InformationIcon message={t('help.dates.deadlines')} />
          </div>
          <div className={styles.datesDeadlines}>
            <TitleValue identifier='dates-initialDeadline' title={t('details.dates.initialDeadline')} value={plazoInicial} viewStyle={{padding: '0 16px'}}/>
            <TitleValue identifier='dates-updatedDeadline' title={t('details.dates.updatedDeadline')} value={plazoActualizadoValue} viewStyle={{padding: '0 16px'}}/>
            <div className={styles.space} />
            <div className={styles.space} />
          </div>
          <div id='dates-finish' className={styles.heading}>{t('details.dates.finish')}
            <InformationIcon message={t('help.dates.finish')} />
          </div>
          <div className={styles.datesFinish}>
            <TitleValue identifier='dates-initialFinish' title={t('details.dates.initialFinish')} value={obra.fechasPlazos.fecFinalizacionInicial || t('details.notInformed')} viewStyle={{padding: '0 16px'}}/>
            <TitleValue identifier='dates-updatedFinish' title={t('details.dates.updatedFinish')} value={obra.fechasPlazos.fecFinalizacionActualizada || t('details.notInformed')} viewStyle={{padding: '0 16px'}}/>
            <TitleValue type='date' handleChange={(data) => handleChange('fecActaRecepcion', data)} minDate={null} maxDate={null} editable={isEditable('fecActaRecepcion')} identifier='dates-reception' title={t('details.dates.reception')} value={obra.fechasPlazos.fecActaRecepcion || '--'} error={isFieldError('fecActaRecepcion')} viewStyle={{padding: '0 16px'}}/>
            <TitleValue type='date' handleChange={(data) => handleChange('fecFinEjecucion', data)} minDate={null} maxDate={null} editable={isEditable('fecFinEjecucion')} identifier='dates-execFinish' title={t('details.dates.execFinish')} value={obra.fechasPlazos.fecFinEjecucion || '--'} error={isFieldError('fecFinEjecucion')} viewStyle={{padding: '0 16px'}}/>
          </div>
        </div>
      }
      <div className='footer' style={{marginBottom: '75px'}}></div>
    </div>
  );
};

export default Dates;
